/**
 * Pod Today API — attendance, updates, day feed, polling.
 * See VIZDAY_POD_TODAY_SPEC.md v1.0.
 */
import { fetchApi } from './apiBase';

// === Types ===

export interface PodTodayParticipant {
  id: number;
  name: string;
}

export interface PodTodayStatus {
  active: boolean;
  pod_names?: string[];
  participants?: PodTodayParticipant[];
  plan_title?: string;
  plan_destination?: string;
}

export interface PodTodayActivity {
  id: number;
  name: string;
  time: string | null;
  time_raw: string | null;
  location: string;
  duration: string;
  notes: string;
  type: string;
  attendance: Record<string, 'attending' | 'opted_out'>;
  completion_state: string;
  activity_status: string;
  is_favorite: boolean;
  update_count: number;
}

export interface PodTodayDayActivities {
  activities: PodTodayActivity[];
  participants: PodTodayParticipant[];
  date: string;
}

export interface ActivityUpdateEntry {
  id: string;
  activity_id: number;
  activity_name: string;
  user_id: number;
  user_name: string;
  type: 'late' | 'change' | 'note';
  text: string;
  duration_minutes: number | null;
  created_at: string;
}

export interface DayFeedResponse {
  entries: ActivityUpdateEntry[];
  date: string;
  count: number;
}

export interface PollResponse {
  activities: Array<{
    id: number;
    attendance: Record<string, string> | null;
    completion_state: string;
    activity_status: string;
  }>;
  new_updates: ActivityUpdateEntry[];
  server_time: string;
}

// === API Functions ===

export async function getPodTodayStatus(planId: number): Promise<PodTodayStatus> {
  return fetchApi<PodTodayStatus>(`/api/planner/itineraries/${planId}/pod-today/`);
}

export async function getPodTodayActivities(planId: number, date?: string): Promise<PodTodayDayActivities> {
  const qs = date ? `?date=${date}` : '';
  return fetchApi<PodTodayDayActivities>(`/api/planner/itineraries/${planId}/pod-today/activities/${qs}`);
}

export async function toggleAttendance(
  planId: number,
  itemId: number,
  status: 'attending' | 'opted_out',
): Promise<{ attendance: Record<string, string> }> {
  return fetchApi(`/api/planner/itineraries/${planId}/items/${itemId}/attendance/`, {
    method: 'POST',
    body: JSON.stringify({ status }),
  });
}

export async function postActivityUpdate(
  planId: number,
  itemId: number,
  type: 'late' | 'change' | 'note',
  text: string,
  durationMinutes?: number,
): Promise<ActivityUpdateEntry> {
  return fetchApi(`/api/planner/itineraries/${planId}/items/${itemId}/update/`, {
    method: 'POST',
    body: JSON.stringify({
      type,
      text,
      duration_minutes: durationMinutes,
    }),
  });
}

export async function getDayFeed(planId: number, date?: string): Promise<DayFeedResponse> {
  const qs = date ? `?date=${date}` : '';
  return fetchApi<DayFeedResponse>(`/api/planner/itineraries/${planId}/pod-today/feed/${qs}`);
}

export async function pollPodToday(planId: number, date: string, since?: string): Promise<PollResponse> {
  let qs = `?date=${date}`;
  if (since) qs += `&since=${encodeURIComponent(since)}`;
  return fetchApi<PollResponse>(`/api/planner/itineraries/${planId}/pod-today/poll/${qs}`);
}
